// src/components/dialogs/InviteByEmailDialog.jsx
import * as React from 'react'
import { Card, Grid, Typography, Box, Chip } from '@mui/material'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import CommonModal from '../atoms/modal/CommonModal'
import ButtonCustom from '../atoms/button/ButtonCustom'
import styles from '../../pages/members/members.module.scss'
import InputCustom from '../atoms/input/InputCustom'
import CheckBoxCustom from '../atoms/checkbox/CheckBox'
import SwitchBtn from '../atoms/switch-btn/SwicthBtn'
import { toastHelper } from '../../util/toastHelper'
import { EMAIL_RE } from '../../util/constants'

const defaultPerms = [
	{ label: 'Mood board', value: false, fieldName: 'moodBoard' },
	{ label: 'Tasks', value: false, fieldName: 'tasks' },
	{ label: 'Budget', value: false, fieldName: 'budget' },
	{ label: 'Vendor Tracker', value: false, fieldName: 'vendorTracker' },
	{ label: 'Guests', value: false, fieldName: 'guests' },
	{ label: 'Timeline', value: false, fieldName: 'timeline' },
]

export default function InviteModal({
	open,
	onClose,
	onSubmit,
	title,
	subtitle,
	withRole = false,
	withWeddingMember = false,
	withRoleInWedding = false,
	roleOptions = ['User'],
	maxRows = 10,
	initialRows = 1,
	submitLabel = 'Send Invite',
	loading = false,
	type,
	roleLabel,
}) {
	const [permissions, setPermissions] = React.useState(defaultPerms)
	const makeNames = {
		lastName: '',
		firstName: '',
		lastNameError: '',
		firstNameError: '',
		firstNameError: false,
		lastNameError: false,
	}
	const makeRow = () => ({
		id: crypto.randomUUID(),
		email: '',
		role: roleOptions[0] ?? '',
		roleInWedding: '',
		isWeddingMember: true, // TODO: change to false when we can start inviting guests (guest RSVP implemented)
		error: '',
		emailError: false,
		...(type === 'staff' ? { ...makeNames } : {}),
	})
	const [rows, setRows] = React.useState(
		Array.from({ length: initialRows }, makeRow)
	)

	React.useEffect(() => {
		if (open) {
			setRows(Array.from({ length: initialRows }, makeRow))
			setPermissions(defaultPerms)
		}
	}, [open])

	const emailsLower = rows
		.map((r) => r.email.trim().toLowerCase())
		.filter(Boolean)

	const validateRow = (idx, nextEmail) => {
		const email = nextEmail.trim()
		if (!email) return ''
		if (!EMAIL_RE.test(email)) return 'Invalid email'
		const lower = email.toLowerCase()
		const count = emailsLower.filter((e) => e === lower).length
		const alsoExistsElsewhere =
			emailsLower.filter((e, i) => e === lower && i !== idx).length > 0
		if (count > 1 || alsoExistsElsewhere) return 'Duplicate email'
		return ''
	}

	const setFirstName = (idx, e) => {
		const firstName = e.target.value.replace(/[^a-zA-Z]/g, '')
		setRows((prev) => {
			const next = [...prev]
			next[idx] = {
				...next[idx],
				firstName,
				firstNameError: !firstName.trim(),
			}
			return next
		})
	}

	const setLastName = (idx, e) => {
		const lastName = e.target.value.replace(/[^a-zA-Z]/g, '')
		setRows((prev) => {
			const next = [...prev]
			next[idx] = {
				...next[idx],
				lastName,
				lastNameError: !lastName.trim(),
			}
			return next
		})
	}

	const setEmail = (idx, email) => {
		setRows((prev) => {
			const next = [...prev]
			next[idx] = { ...next[idx], email, error: '', emailError: false }
			return next
		})
	}

	const setRoleInWedding = (idx, role) => {
		setRows((prev) => {
			const next = [...prev]
			next[idx] = { ...next[idx], roleInWedding: role }
			return next
		})
	}

	const setIsWeddingMember = (idx, isWeddingMember) => {
		setRows((prev) => {
			const next = [...prev]
			next[idx] = { ...next[idx], isWeddingMember }
			return next
		})
	}

	const setRole = (idx, role) => {
		setRows((prev) => {
			const next = [...prev]
			next[idx] = { ...next[idx], role }
			return next
		})
	}

	const addRow = () => {
		if (type === 'staff') validate()
		setRows((prev) =>
			prev.length >= maxRows ? prev : [...prev, makeRow()]
		)
	}

	const removeRow = (id) => {
		setRows((prev) => prev.filter((r) => r.id !== id))
	}

	const cleaned = rows
		.map((r, idx) => ({
			...(type === 'staff'
				? { firstName: r.firstName.trim(), lastName: r.lastName.trim() }
				: {}),
			email: r.email.trim(),
			role: r.role,
			roleInWedding: (r.roleInWedding ?? '').trim(),
			isWeddingMember: !!r.isWeddingMember,
			error: validateRow(idx, r.email),
		}))
		.filter((r) => r.email)

	const hasErrors = cleaned.some((r) => r.error || !r.email)
	const hasStaffErrors = cleaned.some(
		(r) => r.error || !r.firstName || !r.lastName || !r.email
	)
	const nothingToSend = cleaned.length === 0
	const canSubmit = !hasErrors && !nothingToSend && !loading
	const canStaffSubmit = !hasStaffErrors && !nothingToSend && !loading

	const validate = () =>
		setRows((prev) =>
			prev.map((r, idx) => ({
				...r,
				emailError: !r.email?.trim(),
				error: validateRow(idx, r.email),
				lastNameError: !r.lastName,
				firstNameError: !r.firstName,
			}))
		)
	const handleSubmit = async () => {
		validate()
		if (type === 'staff' && !canStaffSubmit) return
		else if (!canSubmit) return
		let mappedPermissions = {}
		for (const permission of permissions) {
			mappedPermissions[permission.fieldName] = permission.value
		}
		await onSubmit?.(
			cleaned.map((r) => ({
				...(type === 'staff'
					? { firstName: r.firstName, lastName: r.lastName }
					: {}),
				email: r.email,
				role: r.role,
				roleInWedding: r.roleInWedding,
				isWeddingMember: r.isWeddingMember,
				permissions: mappedPermissions,
			}))
		)
	}
	const fileInputRef = React.useRef(null)

	const handleCSVClick = () => {
		fileInputRef.current.click()
	}

	const handleFileChange = (event) => {
		const file = event.target.files[0]
		if (file) {
			console.log('Selected file:', file)
		}
	}

	const role_options = [
		{ value: 'user', label: 'user' },
		{ value: 'wedding planner', label: 'wedding planner' },
		{ value: 'bride', label: 'bride' },
	]

	const handleSwitch = (v, label) => {
		setPermissions((prev) =>
			prev.map((p) => (p.label === label ? { ...p, value: v } : p))
		)
	}

	const handleSelectAll = () => {
		setPermissions((prev) => prev.map((p) => ({ ...p, value: true })))
	}

	const handleClearAll = () => {
		setPermissions((prev) => prev.map((p) => ({ ...p, value: false })))
	}

	return (
		<>
			<CommonModal
				open={open}
				handleClose={onClose}
				heading={title}
				subHeading={subtitle}
				footerContent={
					<>
						{/* <ButtonCustom
							onClick={onClose}
							disabled={loading}
							variant="outlined"
							size="lg"
						>
							Cancel
						</ButtonCustom> */}
						<ButtonCustom
							variant="default"
							type="submit"
							size="sm"
							// disabled={!canSubmit}
							isLoading={loading}
							data-cy="send-invites-button"
							onClick={handleSubmit}
						>
							<img src="/images/send.svg" alt="send" />
							{submitLabel}
						</ButtonCustom>
					</>
				}
			>
				<div
					className={styles.InviteModalInner}
					data-cy="invite-modal-content"
				>
					<div
						className={styles.csvAddMemberRow}
						direction="row"
						sx={{ mb: 1 }}
					>
						{/* 
						<Typography variant="caption" color="text.secondary">
							{rows.length}/{maxRows}
						</Typography> */}

						{/* <ButtonCustom
							variant="outlined"
							onClick={handleCSVClick}
						>
							<img
								src="/images/document-upload.svg"
								alt="document-upload.svg"
							/>
							<Typography variant="body">
								Upload CSV file
							</Typography>
							<input
								type="file"
								accept=".csv"
								ref={fileInputRef}
								style={{ display: 'none' }}
								onChange={handleFileChange}
							/>
						</ButtonCustom> */}
						<ButtonCustom
							variant="default"
							size="md"
							onClick={addRow}
							disabled={rows.length >= maxRows}
							data-cy="add-row-button"
						>
							+ Add Row
						</ButtonCustom>
					</div>
					<div className={styles.addMemberFieldWrp}>
						<div
							className={`${styles.addMemberFieldRow} addMemberFieldRow `}
							data-cy="invite-rows-container"
						>
							{rows.map((row, idx) => (
								<div key={row.id} className={styles.row}>
									{type === 'staff' && (
										<>
											<InputCustom
												type="text"
												value={row.firstName ?? ''}
												onChange={(e) =>
													setFirstName(idx, e)
												}
												placeholder="First Name"
												error={row.firstNameError}
												className={styles.input}
												sx={{
													'& .MuiFormHelperText-root':
														{
															position:
																'absolute',
															bottom: '20px',
														},
												}}
											/>
											<InputCustom
												type="text"
												value={row.lastName ?? ''}
												onChange={(e) =>
													setLastName(idx, e)
												}
												placeholder="Last Name"
												error={row.lastNameError}
												className={styles.input}
												sx={{
													'& .MuiFormHelperText-root':
														{
															position:
																'absolute',
															bottom: '20px',
														},
												}}
											/>
										</>
									)}
									<InputCustom
										data-cy={`email-input-${idx}`}
										type="email"
										value={row.email}
										onChange={(e) =>
											setEmail(idx, e.target.value)
										}
										error={
											Boolean(row.error) ||
											Boolean(row.emailError)
										}
										helperText={row.error || ' '}
										placeholder="user@example.com"
										inputProps={{
											'data-cy': `email-input-field-${idx}`,
										}}
										autoFocus={idx === 0}
										className={styles.input}
										sx={{
											'& .MuiFormHelperText-root': {
												position: 'absolute',
												bottom: '20px',
											},
										}}
									/>
									{withWeddingMember && (
										<CheckBoxCustom
											onClick={() =>
												toastHelper(
													"Only add party members for now. Guest RSVP's coming soon!",
													'info'
												)
											}
											disabled // TODO: remove when we can add guests
											label="Is Wedding Member?"
											labelColor="#98A2B1"
											checked={row.isWeddingMember}
											onChange={() =>
												setIsWeddingMember(
													idx,
													!row.isWeddingMember
												)
											}
											style={{ width: '70%' }}
										/>
									)}
									{withRoleInWedding && (
										<InputCustom
											value={row.roleInWedding}
											onChange={(e) =>
												setRoleInWedding(
													idx,
													e.target.value
												)
											}
											type="text"
											placeholder="Role in Wedding"
											className={styles.input}
											sx={{
												'& .MuiFormHelperText-root': {
													position: 'absolute',
													bottom: '20px',
												},
											}}
										/>
									)}

									{withRole && (
										<SelectCustom
											value={row.role}
											onChange={(e) =>
												setRole(idx, e.target.value)
											}
											placeholder="Select Role"
											options={roleOptions}
											label="Role"
											data-cy={`role-select-${idx}`}
											inputProps={{
												'data-cy': `role-select-field-${idx}`,
											}}
										/>
									)}

									{withRoleInWedding && (
										<Box
											sx={{
												width: 160,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											<Chip
												size="small"
												label={roleLabel}
											/>
										</Box>
									)}

									<div className={styles.deleteBtn}>
										<ButtonCustom
											variant="outlined"
											size="lg"
											label="Role"
											onClick={() => removeRow(row.id)}
											disabled={rows.length === 1}
										>
											<img
												data-cy="deleteRow"
												src="/images/trash.svg"
											/>
										</ButtonCustom>
									</div>
								</div>
							))}
						</div>
					</div>

					{type === 'guest' && (
						<Card className={styles.permissionsCard}>
							<div className={styles.permissionsHeader}>
								<Typography
									variant="h6"
									className={styles.permissionsTitle}
								>
									Permissions
								</Typography>
								<div className={styles.permissionsActions}>
									<ButtonCustom
										variant="outlined"
										onClick={handleSelectAll}
										className={styles.actionBtn}
									>
										Select All
									</ButtonCustom>
									<ButtonCustom
										variant="outlined"
										onClick={handleClearAll}
										className={styles.actionBtn}
									>
										Clear All
									</ButtonCustom>
								</div>
							</div>

							<div className={styles.permissionsList}>
								{permissions.map((p) => (
									<div
										key={p.label}
										className={styles.permissionItem}
									>
										<Typography
											variant="body1"
											className={styles.permissionLabel}
										>
											{p.label}
										</Typography>
										<SwitchBtn
											checked={p.value}
											setChecked={(e) =>
												handleSwitch(e, p.label)
											}
										/>
									</div>
								))}
							</div>
						</Card>
					)}
				</div>
			</CommonModal>
		</>
	)
}
